import type { AccessReportRowDTO } from '@webip/shared/src/dto/access_report';

/** Alvo de regra sugerido a partir de uma linha do relatório. */
export type AlvoRegra = {
    type: 'https' | 'domain' | 'cidr' | 'asn';
    target: string;
    label: string;
    hint?: string;
};

// Segundo nível usado por registros de país (example.com.br, example.co.uk):
// sem isso, "*.com.br" seria o "domínio" de qualquer site brasileiro.
const SEGUNDO_NIVEL = new Set(['com', 'net', 'org', 'gov', 'edu', 'co', 'ac', 'ne', 'or', 'go', 'mil', 'art', 'adv', 'ind', 'inf', 'eng', 'blog', 'app', 'dev', 'tv', 'wiki']);

/** Domínio registrável aproximado: "a.b.example.com.br" → "example.com.br". */
export function dominioRegistravel(host: string): string {
    const labels = host.toLowerCase().replace(/\.$/, '').split('.').filter(Boolean);
    if (labels.length <= 2) return labels.join('.');
    const tld = labels[labels.length - 1];
    const sld = labels[labels.length - 2];
    const n = tld.length === 2 && SEGUNDO_NIVEL.has(sld) ? 3 : 2;
    return labels.slice(-n).join('.');
}

const IPV4 = /^(\d{1,3}\.){3}\d{1,3}$/;

/**
 * Alvos possíveis para uma regra criada a partir da linha, o padrão primeiro.
 *
 * - SNI: o nome que o cliente pediu — navegação HTTPS (tls-host) exata ou com
 *   subdomínios. Sem `https` (OPNsense não tem matcher de SNI), vira domínio.
 * - PTR: nome do provedor, não do site; só o domínio registrável com
 *   subdomínios faz sentido ("*.1e100.net"), e mesmo assim só casa se os
 *   clientes resolverem nomes dele — IP, bloco e AS seguem como alternativa.
 * - IP: o próprio IP, o bloco anunciado e o AS inteiro.
 */
export function opcoesAlvo(row: AccessReportRowDTO, opts: { https: boolean }): AlvoRegra[] {
    const out: AlvoRegra[] = [];
    const add = (a: AlvoRegra) => {
        if (!out.some((o) => o.type === a.type && o.target === a.target)) out.push(a);
    };

    const host = row.destinationDomain?.toLowerCase().replace(/\.$/, '');
    const nomeValido = !!host && !IPV4.test(host) && host.includes('.');
    if (nomeValido && host) {
        const reg = dominioRegistravel(host);
        if (row.destinationDomainSource === 'ptr') {
            add({
                type: 'domain',
                target: `*.${reg}`,
                label: `Domínio *.${reg}`,
                hint: `Nome do DNS reverso (${host}) — costuma ser do provedor; a regra só casa se os clientes resolverem nomes desse domínio.`,
            });
        } else {
            // SNI, proxy (domínio pedido ao Squid) ou linha antiga sem origem.
            const sni = row.destinationDomainSource === 'sni' && row.type !== 'proxy';
            const type = sni && opts.https ? 'https' : 'domain';
            const tipoLabel = type === 'https' ? 'Navegação HTTPS' : 'Domínio';
            add({ type, target: host, label: `${tipoLabel} ${host}` });
            const pai = host.split('.').slice(1).join('.');
            if (pai.length > reg.length) add({ type, target: `*.${pai}`, label: `${tipoLabel} *.${pai}` });
            add({ type, target: `*.${reg}`, label: `${tipoLabel} *.${reg}` });
        }
    }

    const ip = row.destinationIp;
    if (ip && IPV4.test(ip)) {
        add({ type: 'cidr', target: ip, label: `Somente o IP ${ip}` });
        if (row.asnPrefix && !row.asnPrefix.endsWith('/32')) {
            add({
                type: 'cidr',
                target: row.asnPrefix,
                label: `Bloco ${row.asnPrefix}${row.asnName ? ` (${row.asnName})` : ''}`,
            });
        }
        if (row.asn) {
            add({
                type: 'asn',
                target: String(row.asn),
                label: `Todo o AS${row.asn}${row.asnName ? ` (${row.asnName})` : ''}`,
                hint: 'Inclui todos os endereços do provedor, não só este serviço.',
            });
        }
    }
    return out;
}

/** A linha tem algo que vire alvo de regra (login de hotspot não tem). */
export function linhaAceitaRegra(row: AccessReportRowDTO): boolean {
    return !!(row.destinationIp || row.destinationDomain) && row.type !== 'scan';
}
